import type {
  IAfterVisitSummaryOperationError,
  IAfterVisitSummaryOperationResult,
  IAfterVisitSummaryRepository,
  IAfterVisitSummaryService,
} from "../ports/AfterVisitSummary";

const operationError = (error: unknown): IAfterVisitSummaryOperationError => {
  if (error instanceof Error) {
    const repositoryError = error as Error & {
      status?: unknown;
      detail?: unknown;
    };
    return {
      message: repositoryError.message,
      status:
        typeof repositoryError.status === "number"
          ? repositoryError.status
          : undefined,
      detail: repositoryError.detail,
    };
  }

  return {
    message: "An unknown after-visit summary error occurred",
  };
};

const execute = async <T>(
  operation: () => Promise<T>
): Promise<IAfterVisitSummaryOperationResult<T>> => {
  try {
    return { success: true, data: await operation() };
  } catch (error) {
    return { success: false, error: operationError(error) };
  }
};

export const AfterVisitSummaryServiceImplFactory = ({
  afterVisitSummaryRepository,
}: {
  afterVisitSummaryRepository: IAfterVisitSummaryRepository;
}): IAfterVisitSummaryService => ({
  startAfterVisitSummary: (scope, request) =>
    execute(() =>
      afterVisitSummaryRepository.startAfterVisitSummaryAPI(scope, request)
    ),

  getAfterVisitSummary: (scope) =>
    execute(() => afterVisitSummaryRepository.getAfterVisitSummaryAPI(scope)),

  editAfterVisitSummary: (scope, request) =>
    execute(() =>
      afterVisitSummaryRepository.editAfterVisitSummaryAPI(scope, request)
    ),

  downloadAfterVisitSummaryPdf: (scope) =>
    execute(() =>
      afterVisitSummaryRepository.downloadAfterVisitSummaryPdfAPI(scope)
    ),

  getHchbCoordinationNoteTypes: (clinicId, patientId) =>
    execute(() =>
      afterVisitSummaryRepository.getHchbCoordinationNoteTypesAPI(
        clinicId,
        patientId
      )
    ),

  sendAfterVisitSummary: (scope, request) =>
    execute(() =>
      afterVisitSummaryRepository.sendAfterVisitSummaryAPI(scope, request)
    ),

  retryCoordinationNote: (clinicId, patientId, coordinationNoteId) =>
    execute(() =>
      afterVisitSummaryRepository.retryCoordinationNoteAPI(
        clinicId,
        patientId,
        coordinationNoteId
      )
    ),
});
